import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PAGE_SIZE, type PagedResult } from "../../../../components/PortfolioPerformance/pager";
import { useTicketSize } from "../ticket-size";
import { apiGet, apiPost } from "./client";
import type { SensitivityTier } from "./shared";

export type { SensitivityTier };

export interface MacroFactor {
  factor: string;
  weight: number; // mean |SHAP| for this segment's score
  changePct: number | null; // change vs the previous quarter, percent
}

export interface IndustryPoint {
  id: string;
  name: string;
  sensitivity: number; // 0-100, y-axis
  volume: number; // $ owned NI, drives bubble size (merged in from useMacroVolumes)
  tier: SensitivityTier;
  change: number | null; // score change vs the previous quarter
  factors: MacroFactor[];
}

export interface MacroExposureGroup {
  tierCounts: Array<{ tier: SensitivityTier; count: number }>;
  points: IndustryPoint[];
}

// Every Macro Exposure read is for one ticket band (the page toggle; "all" = the industry scores). Each band is scored
// within itself (Micro industries ranked against each other). The quarter is always the latest INDUSTRY quarter, so a
// band with no scores that quarter -- Mid-market / Large today, the scored book is Under 250K -- comes back with no
// points and hasData false; the page shows a no-data state for it instead of the charts.
export interface TicketScoped {
  ticketSize: string; // "all" | "micro" | "small" | "mid_market" | "large"
  hasData: boolean;
}

export interface MacroExposureSummary extends TicketScoped {
  asOf: string;
  asset: MacroExposureGroup;
  borrower: MacroExposureGroup;
}

// null when the athena_decisions views have no rows yet.
export function useMacroExposure() {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["macro-exposure", "scores", ticket ?? "all"],
    queryFn: () => apiGet<MacroExposureSummary | null>("/macro-exposure", { ticket }),
    placeholderData: keepPreviousData,
  });
}

// A macro variable, picked from athena_decisions.macro_sensitivity_factor_delta and averaged over the industries
// scored in the quarter: `level` is its average scaled value, `change` the average movement since the previous
// quarter (only industries that have both quarters count), `previousLevel` the average it moved from.
export interface MacroVariableQuarter {
  quarter: string;
  label: string; // "Jun'26"
  level: number | null; // null: the variable was not in that quarter
  change: number | null; // null when there is no previous quarter to compare
}

export interface MacroVariablePoint {
  id: string;
  name: string; // the model's feature name; the page labels it
  level: number | null;
  change: number | null;
  previousLevel: number | null;
  segments: number; // how many industries the averages cover
  history: MacroVariableQuarter[]; // last 4 quarters, oldest first
}

export interface MacroVariablesSummary extends TicketScoped {
  asOf: string;
  points: MacroVariablePoint[];
}

// null when the factor view has no rows yet.
export function useMacroVariables() {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["macro-exposure", "variables", ticket ?? "all"],
    queryFn: () => apiGet<MacroVariablesSummary | null>("/macro-exposure/variables", { ticket }),
    placeholderData: keepPreviousData,
  });
}

// Quarter-on-quarter change of the biggest-moving macro variables (up to 10, ranked by the latest quarter's absolute
// change), from athena_decisions.macro_sensitivity_factor_delta. Each series has one point per quarter, oldest first;
// `change` is null where that quarter had nothing to compare against.
export interface MacroTrendPoint {
  quarter: string;
  label: string; // "Jun'26"
  change: number | null;
  level: number | null;
}

export interface MacroTrendSeries {
  id: string;
  name: string; // the model's feature name; the page labels it
  points: MacroTrendPoint[];
}

// "year": one point per calendar year, that year's last scored quarter against the same quarter a year earlier (the
// current year is labelled "YTD"). "quarter": one point per quarter, against the previous quarter. Leading x entries
// with no figure for any variable are left off, so the axis starts where the lines do.
export type TrendPeriod = "year" | "quarter";

export interface MacroVariableTrend extends TicketScoped {
  asOf: string;
  period: TrendPeriod;
  quarters: Array<{ quarter: string; label: string }>;
  series: MacroTrendSeries[];
}

// null when the factor view has no rows yet.
export function useMacroVariableTrend(period: TrendPeriod = "year") {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["macro-exposure", "variables", "trend", period, ticket ?? "all"],
    queryFn: () =>
      apiGet<MacroVariableTrend | null>("/macro-exposure/variables/trend", {
        period,
        ...(period === "year" ? { years: "5" } : { quarters: "8" }),
        ticket,
      }),
    placeholderData: keepPreviousData,
  });
}

// The 0-100 sensitivity score averaged over the segments scored in each year or quarter (athena_decisions.macro_sensitivity_period_avg),
// oldest first. A year with fewer than four quarters scored is the year so far and its label says "YTD"; `segmentsScored` / `quartersScored`
// say what the average covers. null when that table is not there or is empty; hasData false for a ticket band with no rows.
export interface MacroPeriodPoint {
  label: string; // "2025", "2026 YTD", "Q3'26"
  periodStart: string;
  score: number;
  segmentsScored: number;
  quartersScored: number;
}

export interface MacroPeriodAverage extends TicketScoped {
  asOf: string | null;
  period: TrendPeriod;
  points: MacroPeriodPoint[];
}

export function useMacroPeriodAverage(period: TrendPeriod = "year") {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["macro-exposure", "average", period, ticket ?? "all"],
    queryFn: () => apiGet<MacroPeriodAverage | null>("/macro-exposure/average", { period, ticket }),
    placeholderData: keepPreviousData,
  });
}

// Owned NI by industry name, for the deals in the chosen ticket band; fetched separately so the scores paint without
// waiting for it.
export interface MacroVolumes {
  asset: Record<string, number>;
  borrower: Record<string, number>;
}

export function useMacroVolumes() {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["macro-exposure", "volumes", ticket ?? "all"],
    queryFn: () => apiGet<MacroVolumes>("/macro-exposure/volumes", { ticket }),
    staleTime: 15 * 60 * 1000,
  });
}

export interface MacroTier {
  tier: SensitivityTier;
  minScore: number;
}

export interface MacroTierGrid {
  tiers: MacroTier[];
  hasPending: boolean;
}

export interface MacroTierProposal {
  id: string;
  currentGrid: MacroTier[];
  proposedGrid: MacroTier[];
  reason: string;
  proposedBy: string | null;
  proposedAt: string;
  status: "pending" | "approved" | "rejected";
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
}

export function useMacroTiers() {
  return useQuery({
    queryKey: ["macro-exposure", "tiers"],
    queryFn: () => apiGet<MacroTierGrid>("/macro-exposure/tiers"),
  });
}

export function useMacroTierLog(offset: number) {
  return useQuery({
    queryKey: ["macro-exposure", "tier-log", offset],
    queryFn: () =>
      apiGet<PagedResult<MacroTierProposal>>("/macro-exposure/tiers/log", {
        limit: String(PAGE_SIZE),
        offset: String(offset),
      }),
    placeholderData: keepPreviousData,
  });
}

// An approval re-tiers the charts, so everything under "macro-exposure" refreshes except the (slow) volumes.
function useRefresh() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: ["macro-exposure"],
      predicate: (q) => q.queryKey[1] !== "volumes",
    });
}

export function useProposeMacroTiers() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (draft: { tiers: MacroTier[]; reason: string }) =>
      apiPost<MacroTierProposal>("/macro-exposure/tiers/proposals", draft),
    onSuccess: refresh,
  });
}

export function useDecideMacroTiers() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (args: { id: string; status: "approved" | "rejected"; notes?: string }) =>
      apiPost<MacroTierProposal>(`/macro-exposure/tiers/proposals/${args.id}/decide`, {
        status: args.status,
        notes: args.notes,
      }),
    onSuccess: refresh,
  });
}
